import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Crosshair, CheckCircle, ShieldWarning, Sparkle } from "@phosphor-icons/react";

export const mitreState = (c) => c?.mitre_override_at ? "overridden" : (c?.mitre_techniques || []).length ? "mapped"
  : (c?.mitre_tactics || []).length ? "tactic_only" : c?.mitre_unmappable ? "not_mappable" : "none";

const STATE_LABEL = { mapped: "MITRE: mapped", tactic_only: "MITRE: tactic only", not_mappable: "MITRE: not mappable", overridden: "MITRE: overridden" };
const STATE_CLS = {
  mapped: "border-rag-green-fg/40 text-rag-green-fg", tactic_only: "border-rag-amber/50 text-rag-amber-fg",
  not_mappable: "border-border text-muted-foreground", overridden: "border-rag-red/40 text-rag-red-fg",
};

export function MitreBadge({ c, lead, overriderName }) {
  const st = mitreState(c);
  if (st === "none") return null;
  let title;
  if (st === "overridden" && lead) title = `Overridden by ${overriderName || c.mitre_override_by || "lead"}: ${c.mitre_override_reason || ""}`;
  else if (st === "not_mappable") title = c.mitre_unmappable_reason || "";
  else if (st === "tactic_only") title = (c.mitre_tactics || []).join(", ");
  return <span data-testid="soc-mitre-badge" title={title} className={`text-[10px] font-mono px-2 py-0.5 border uppercase ${STATE_CLS[st]}`}>{STATE_LABEL[st]}</span>;
}

// Shown when Resolved/Closed is attempted on a true positive with no mapping.
export function MitreGateDialog({ open, onClose, c, mitreAll, lead, minLen = 10, onPatch, onProceed }) {
  const [mode, setMode] = useState("technique");
  const [q, setQ] = useState("");
  const [tactics, setTactics] = useState([]);
  const [selTactics, setSelTactics] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggested, setSuggested] = useState([]);
  useEffect(() => { if (open) { setMode("technique"); setQ(""); setReason(""); setSelTactics(c?.mitre_tactics || []); api.get("/soc/mitre/tactics").then(r => setTactics(r.data)).catch(() => {}); if (c?.id) api.get(`/soc/cases/${c.id}/mitre-suggestions`).then(r => setSuggested(r.data)).catch(() => setSuggested([])); } }, [open]); // eslint-disable-line
  if (!c) return null;
  const matches = q.length >= 2 ? (mitreAll || []).filter(t => `${t.id} ${t.name}`.toLowerCase().includes(q.toLowerCase())).slice(0, 30) : [];
  const picked = c.mitre_techniques || [];
  const toggleTech = async (tid) => { const next = picked.includes(tid) ? picked.filter(x => x !== tid) : [...picked, tid]; await onPatch({ mitre_techniques: next }); };
  const toggleTactic = (t) => setSelTactics(s => s.includes(t) ? s.filter(x => x !== t) : [...s, t]);
  const reasonOk = reason.trim().length >= minLen;
  const canProceed = mode === "technique" ? picked.length > 0 : mode === "tactic" ? selTactics.length > 0 : reasonOk;
  const proceed = async () => {
    setBusy(true);
    try {
      if (mode === "tactic") { await onPatch({ mitre_tactics: selTactics }); await onProceed({}); }
      else if (mode === "unmappable") await onProceed({ mitre_unmappable: true, mitre_unmappable_reason: reason.trim() });
      else if (mode === "override") await onProceed({ mitre_override_reason: reason.trim() });
      else await onProceed({});
      onClose();
    } finally { setBusy(false); }
  };
  const Opt = ({ k, label, hint, testid }) => (
    <button type="button" data-testid={testid} onClick={() => setMode(k)} aria-pressed={mode === k}
      className={`text-left px-3 py-2 rounded-sm border text-xs transition-colors ${mode === k ? "border-primary bg-primary/10" : "border-border hover:border-foreground/40"}`}>
      <div className="font-medium">{label}</div><div className="text-[11px] text-muted-foreground">{hint}</div>
    </button>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl rounded-sm" data-testid="soc-mitre-gate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><ShieldWarning size={18} className="text-rag-amber-fg" /> MITRE ATT&CK mapping required</DialogTitle>
          <DialogDescription>This case is a True Positive. Before it can be resolved or closed, record how the adversary operated — or explain why that isn't possible.</DialogDescription>
        </DialogHeader>
        <div className={`grid gap-2 ${lead ? "grid-cols-2" : "grid-cols-3"}`}>
          <Opt k="technique" testid="soc-gate-opt-technique" label="Add technique(s)" hint="Specific ATT&CK technique IDs" />
          <Opt k="tactic" testid="soc-gate-opt-tactic" label="Tactic only" hint="Technique unknown; tag the tactic" />
          <Opt k="unmappable" testid="soc-gate-opt-unmappable" label="Not mappable" hint="Explain why (reason required)" />
          {lead && <Opt k="override" testid="soc-gate-opt-override" label="Override (lead)" hint="Close without mapping; reason logged" />}
        </div>
        {mode === "technique" && (
          <div className="space-y-2">
            {suggested.filter(s => !picked.includes(s.id)).length > 0 && (
              <div data-testid="soc-gate-suggestions">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1"><Sparkle size={11} className="text-primary" /> Suggested for this case</div>
                <div className="flex flex-wrap gap-1.5">
                  {suggested.filter(s => !picked.includes(s.id)).map(s => (
                    <button key={s.id} type="button" data-testid={`soc-gate-suggest-${s.id}`} onClick={() => toggleTech(s.id)} title={s.reasons.join(" · ")}
                      className="text-left text-[11px] px-2 py-1 rounded-sm border border-primary/40 bg-primary/5 hover:bg-primary/15 transition-colors flex items-center gap-1.5">
                      <span className="font-mono text-primary">{s.id}</span><span className="truncate max-w-[180px]">{s.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="relative"><Crosshair size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input data-testid="soc-gate-tech-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search techniques (e.g. T1566 or phishing)…" className="rounded-sm h-9 pl-8 text-sm" /></div>
            {matches.length > 0 && <div className="max-h-44 overflow-y-auto border border-border rounded-sm divide-y divide-border">
              {matches.map(t => <button key={t.id} data-testid={`soc-gate-tech-${t.id}`} onClick={() => toggleTech(t.id)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent/40 flex items-center gap-2">
                <span className="font-mono text-xs text-primary w-20 shrink-0">{t.id}</span><span className="flex-1 truncate">{t.name}</span>{picked.includes(t.id) && <CheckCircle size={14} className="text-rag-green-fg" />}</button>)}
            </div>}
            <div className="text-xs text-muted-foreground" data-testid="soc-gate-picked">{picked.length ? `Mapped: ${picked.join(", ")}` : "No techniques mapped yet."}</div>
          </div>
        )}
        {mode === "tactic" && (
          <div className="flex flex-wrap gap-1.5" data-testid="soc-gate-tactics">
            {tactics.map(t => <button key={t} type="button" data-testid={`soc-gate-tactic-${t}`} onClick={() => toggleTactic(t)} aria-pressed={selTactics.includes(t)}
              className={`text-[10px] font-mono uppercase px-2 py-1 rounded-sm border transition-colors ${selTactics.includes(t) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{t}</button>)}
          </div>
        )}
        {(mode === "unmappable" || mode === "override") && (
          <div>
            <Textarea data-testid="soc-gate-reason" value={reason} onChange={e => setReason(e.target.value)} rows={3} className="rounded-sm text-sm"
              placeholder={mode === "override" ? "Why is this true positive being closed without an ATT&CK mapping?" : "Why can't this true positive be mapped to ATT&CK?"} />
            <div className={`text-[10px] font-mono mt-1 ${reasonOk ? "text-muted-foreground" : "text-rag-red-fg"}`} data-testid="soc-gate-reason-count">{reason.trim().length}/{minLen} characters minimum</div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm font-mono text-xs uppercase">Cancel</Button>
          <Button data-testid="soc-gate-proceed" onClick={proceed} disabled={!canProceed || busy} className="rounded-sm font-mono text-xs uppercase">{busy ? "Saving…" : "Save & continue"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
